import { FC, useState, useEffect } from "react";
import { Vehicle } from "@data/entities/Vehicle/Vehicle";
import Dropdown from "../../components/Dropdown/Dropdown";
import {
  GridSpacer,
  GridWrapper,
  VehicleDetailForm,
  VehicleDetailFormWrapper,
  TextContainer,
} from "./vehicleDetailWidgetStyle";
import ReadOnlyInput from "@ui/components/Input/ReadOnlyInput";
import { Workspace } from "@data/entities/Workspace/Workspace";
import InputText from "@ui/components/Input/InputText";
import {
  ButtonContainer,
  FooterContainer,
} from "../RegisterVehicleWidget/registerVehicleWidgetStyle";
import DefaultButton from "@ui/components/Button/DefaultButton";
import { mainTheme } from "@data/theme/theme";
import { useNavigate } from "react-router-dom";
import {
  useMutationVehicle,
} from "@data/repositories/Vehicle/mutationVehicle";
import { z } from "zod";
import { DefaultLoadingOverlay } from "@ui/components/LoadingOverlay/DefualtLoadingOverlay";
import { CheckVehicleWithEnvModal } from "@ui/modals/CheckVehicleWithEnvModal";
import { WorkspaceDropdown } from "@data/types";

export type EditedVehicleEnvironment = Vehicle & {
  workspace_m: Pick<Workspace, "workspace_cd" | "workspace_nm" | "company_id">;
  next_vehicle_id: string;
  is_stock: boolean;
};
type validationSchema = Pick<Workspace, "workspace_cd"> & {
  next_vehicle_id: string;
};

// NOTE:
// 一覧APIでは workspace_cd が返らないため、currentWorkspace には
// workspace_nm のみを渡す。
// workspace_nm はユニークである前提で、内部で workspaces から
// 対応する workspace_cd を復元して使用する。
// 将来的にAPIが workspace_cd を返すようになった場合は、
// 識別は workspace_cd に統一すること。
type CurrentWorkspace = {
  workspace_nm: string;
};

type Props = {
  vehicle: Vehicle;
  currentWorkspace?: CurrentWorkspace;
  workspaces?: WorkspaceDropdown[];
};

const initialErrors: validationSchema = {
  workspace_cd: "",
  next_vehicle_id: "",
};

const VehicleDetailWidget: FC<Props> = ({
  vehicle,
  currentWorkspace,
  workspaces = [],
}) => {
  const [errors, setErrors] = useState(initialErrors);
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();
  const { updateVehicleEnvironment } = useMutationVehicle();

  // workspace_nm をキーに Workspace を特定（API制約による暫定対応）
  const initialWorkspace = workspaces?.find(
    (workspace) => workspace.workspace_nm === currentWorkspace?.workspace_nm
  );

  const [editedVehicle, setEditedVehicle] = useState<EditedVehicleEnvironment>({
    ...vehicle,
    next_vehicle_id: vehicle.vehicle_id ?? "",
    workspace_m: {
      workspace_cd: "",
      workspace_nm: "",
      company_id: "",
    },
    is_stock: false,
  });

  useEffect(() => {
    // currentWorkspace.workspace_nm が存在しても workspaces から復元できない場合は、
    // 誤った走行環境を初期選択しないよう未選択のままにして placeholder を表示する。
    if (!initialWorkspace) return;

    setEditedVehicle((prev) => {
      if (prev.workspace_m.workspace_cd) return prev;

      return {
        ...prev,
        workspace_cd: initialWorkspace.workspace_cd ?? "",
        workspace_m: {
          workspace_cd: initialWorkspace.workspace_cd ?? "",
          workspace_nm: initialWorkspace.workspace_nm ?? "",
          company_id: initialWorkspace.company_id ?? "",
        },
      };
    });
  }, [initialWorkspace]);

  const workspaceDropdown = workspaces.map((workspace) => ({
    value: workspace.workspace_cd ?? "",
    label: workspace.workspace_nm ?? "",
  }));

  const onSelect = (value: string | null) => {
    const selected = workspaces.find((w) => w.workspace_cd === value);

    setEditedVehicle((prev) => ({
      ...prev,
      workspace_cd: selected?.workspace_cd ?? "",
      workspace_m: {
        workspace_cd: selected?.workspace_cd ?? "",
        workspace_nm: selected?.workspace_nm ?? "",
        company_id: selected?.company_id ?? "",
      },
    }));
  };

  const throwableSchema = z.object<{
    [key in keyof validationSchema]: any;
  }>({
    workspace_cd: z.string().nonempty("このフィールドは必須です"),
    next_vehicle_id: z
      .string()
      .regex(/^[0-9a-zA-Z-]*$/, "半角英数字、「-」のみ入力できます")
      .nonempty("このフィールドは必須です"),
  });

  const onClick = () => {
    try {
      const workspaceCd = editedVehicle.workspace_m.workspace_cd ?? "";
      const nextVehicleId = editedVehicle.next_vehicle_id ?? "";

      throwableSchema.parse({
        workspace_cd: workspaceCd,
        next_vehicle_id: nextVehicleId,
      });

      setErrors(initialErrors);
      setModalOpen(true);
    } catch (e: any) {
      setErrors({
        workspace_cd: e.flatten().fieldErrors.workspace_cd?.[0] ?? "",
        next_vehicle_id: e.flatten().fieldErrors.next_vehicle_id?.[0] ?? "",
      });
    }
  };

  const [isStockCheck, setIsStockChecked] = useState(false);

  const [prevBeforeStock, setPrevBeforeStock] = useState<{
    workspace_cd: string;
    workspace_nm: string;
    company_id: string;
    next_vehicle_id: string;
  } | null>(null);

  const handleStockCheckBox = () => {
    const isNextStock = !isStockCheck;

    if (isNextStock) {
      // stockに入る前の状態を退避
      setPrevBeforeStock({
        workspace_cd: editedVehicle.workspace_m.workspace_cd ?? "",
        workspace_nm: editedVehicle.workspace_m.workspace_nm ?? "",
        company_id: editedVehicle.workspace_m.company_id ?? "",
        next_vehicle_id: editedVehicle.next_vehicle_id ?? "",
      });

      const stockWorkspace = workspaces.find((w) => w.workspace_cd === "-1");

      setEditedVehicle((prev) => ({
        ...prev,
        workspace_cd: stockWorkspace?.workspace_cd ?? "",
        workspace_m: {
          workspace_cd: stockWorkspace?.workspace_cd ?? "",
          workspace_nm: stockWorkspace?.workspace_nm ?? "",
          company_id: stockWorkspace?.company_id ?? "",
        },
        next_vehicle_id: `stock-vehicle-${prev.user_id}`,
        is_stock: true,
      }));
    } else {
      // stockに入る前の状態へ戻す
      setEditedVehicle((prev) => ({
        ...prev,
        workspace_cd: prevBeforeStock?.workspace_cd ?? "",
        workspace_m: {
          workspace_cd: prevBeforeStock?.workspace_cd ?? "",
          workspace_nm: prevBeforeStock?.workspace_nm ?? "",
          company_id: prevBeforeStock?.company_id ?? "",
        },
        next_vehicle_id: prevBeforeStock?.next_vehicle_id ?? "",
        is_stock: false,
      }));
    }

    setIsStockChecked(isNextStock);
  };

  return (
    <>
      <VehicleDetailFormWrapper>
        <GridSpacer />

        <VehicleDetailForm>
          <GridWrapper>
            <TextContainer>
              <ReadOnlyInput
                label="現在の走行環境名"
                value={currentWorkspace?.workspace_nm}
                size={"md"}
              />
            </TextContainer>
          </GridWrapper>

          <GridWrapper>
            <TextContainer>
              <ReadOnlyInput
                label="現在のFMS車両ID"
                value={vehicle.vehicle_id}
                size={"md"}
              />
            </TextContainer>
          </GridWrapper>

          <GridWrapper>
            <TextContainer>
              <ReadOnlyInput
                label="車両名"
                value={vehicle.alias_name}
                size={"md"}
              />
            </TextContainer>
          </GridWrapper>

          <GridWrapper>
            <TextContainer>
              <label>{"FMS車両ID      "}</label>
              <input
                type="checkbox"
                id="sotkCheckButton"
                checked={isStockCheck}
                onChange={handleStockCheckBox}
              />
              <label>{"在庫環境"}</label>
              <InputText
                withAsterisk
                label=""
                value={editedVehicle.next_vehicle_id}
                size={"md"}
                disabled={editedVehicle.is_stock}
                onChange={(e) => {
                  setEditedVehicle((pre) => ({
                    ...pre,
                    next_vehicle_id: e.target.value,
                  }));
                }}
                placeholder="半角英数で入力"
                description="例： ab***************"
                errors={{
                  error: Boolean(errors.next_vehicle_id),
                  errorMessage: errors.next_vehicle_id,
                }}
              />
            </TextContainer>
          </GridWrapper>

          <GridWrapper>
            <Dropdown
              withAsterisk
              label="走行環境名"
              placeholder="１つ選択"
              options={workspaceDropdown}
              searchable
              onSelect={onSelect}
              value={editedVehicle.workspace_m.workspace_cd || null}
              errorMessage={errors.workspace_cd}
              disabled={editedVehicle.is_stock}
            />
          </GridWrapper>

          <GridSpacer />
          <FooterContainer>
            <GridSpacer />
            <ButtonContainer>
              <DefaultButton
                backgroundColor={"white"}
                hoverColor={mainTheme.colors.secondary}
                color={mainTheme.colors.onSecondary}
                onClick={() => {
                  navigate("/vehicles");
                }}
                buttonText={"キャンセル"}
                size={"M"}
                border
                borderColor={mainTheme.colors.secondaryContainer}
                fontWeight={"600"}
                type="button"
              />
              <DefaultButton
                type="button"
                backgroundColor={mainTheme.colors.primaryPositive}
                hoverColor={mainTheme.colors.primary}
                color={mainTheme.colors.onPrimary}
                onClick={onClick}
                buttonText={"切替"}
                size={"M"}
                contentWidth={"120px"}
              />
            </ButtonContainer>
            <GridSpacer />
          </FooterContainer>
        </VehicleDetailForm>
      </VehicleDetailFormWrapper>

      {modalOpen && (
        <CheckVehicleWithEnvModal
          isOpen={modalOpen}
          editedVehicleEnv={editedVehicle}
          isStockCheck={isStockCheck}
          onRequestClose={() => {}}
          onClose={() => setModalOpen(false)}
        />
      )}

      {updateVehicleEnvironment.isLoading && (
        <DefaultLoadingOverlay visible={true} loaderSize="lg" />
      )}
    </>
  );
};

export default VehicleDetailWidget;
